import type { FieldDescriptor } from '@rammetec/data-access'

function formatEnumAccepted(field: FieldDescriptor): string {
  if (!field.enumValues?.length) return ''
  const labels = field.enumLabels
  const parts = field.enumValues.map((v) => {
    const label = labels?.[v]
    return label && label !== v ? `${v}=${label}` : v
  })
  return parts.join(', ')
}

/**
 * Normalize a pt-BR / plain number string to a JS number string.
 * - Has `,` → dots are thousands (`1.234,56` → `1234.56`)
 * - Only `.` → decimal (`1.234` → `1.234`)
 */
function normalizeNumberInput(raw: string): string {
  const cleaned = raw.replace(/\s/g, '')
  if (cleaned.includes(',')) {
    return cleaned.replace(/\./g, '').replace(',', '.')
  }
  return cleaned
}

/** Coerce a spreadsheet / model string to the form value for this descriptor field. */
export function coerceCellValue(
  field: FieldDescriptor,
  raw: string,
): { ok: true; value: unknown } | { ok: false; error: string } {
  const label = field.label

  switch (field.type) {
    case 'string': {
      return { ok: true, value: raw }
    }
    case 'number': {
      const trimmed = raw.trim()
      if (trimmed === '') return { ok: true, value: null }
      const normalized = normalizeNumberInput(trimmed)
      const n = Number(normalized)
      if (!Number.isFinite(n)) {
        return {
          ok: false,
          error: `valor "${raw}" inválido para "${label}" (número). Exemplos: 250, 12,5`,
        }
      }
      return { ok: true, value: n }
    }
    case 'boolean': {
      const t = raw.trim().toLowerCase()
      if (t === '') return { ok: true, value: false }
      if (['true', '1', 'sim', 'yes', 's'].includes(t)) return { ok: true, value: true }
      if (['false', '0', 'não', 'nao', 'no', 'n'].includes(t)) return { ok: true, value: false }
      return {
        ok: false,
        error: `valor "${raw}" inválido para "${label}" (booleano). Aceitos: sim, não, true, false`,
      }
    }
    case 'date': {
      const trimmed = raw.trim()
      if (trimmed === '') return { ok: true, value: '' }
      const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
      if (iso) return { ok: true, value: trimmed }
      const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed)
      if (br) {
        const d = br[1]!.padStart(2, '0')
        const m = br[2]!.padStart(2, '0')
        const y = br[3]!
        return { ok: true, value: `${y}-${m}-${d}` }
      }
      return {
        ok: false,
        error: `valor "${raw}" inválido para "${label}" (data). Use AAAA-MM-DD ou DD/MM/AAAA`,
      }
    }
    case 'enum': {
      const trimmed = raw.trim()
      if (trimmed === '') return { ok: true, value: null }
      const values = field.enumValues ?? []
      if (values.includes(trimmed)) return { ok: true, value: trimmed }
      const labels = field.enumLabels
      if (labels) {
        for (const [value, valueLabel] of Object.entries(labels)) {
          if (valueLabel === trimmed) return { ok: true, value }
        }
      }
      const accepted = formatEnumAccepted(field) || values.join(', ') || '(nenhum)'
      return {
        ok: false,
        error: `valor "${raw}" inválido para "${label}". Aceitos: ${accepted}`,
      }
    }
    default:
      return {
        ok: false,
        error: `campo "${label}" não aceita preenchimento pelo assistente`,
      }
  }
}

/** Alias kept for assistant call sites (same contract as before the import engine). */
export const coerceFormFieldValue = coerceCellValue
